<template>
  <v-container fluid>
    <!-- Add Category Button -->
    <v-container>
      <v-row align="center" justify="end">
        <v-col cols="12" md="3" class="d-flex justify-end pr-0">
          <v-btn color="cyan" dark depressed small @click="openAddDialog">
            <v-icon left>mdi-plus</v-icon>
            Add Category
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <div style="max-height: 67vh; overflow-y: auto">
      <!-- Category Listing -->
      <ListingTable
        :column-count="4"
        :data-loading="dataLoading"
        :row-data="dataRows"
      >
        <template v-slot:thead>
          <thead>
            <tr>
              <th class="simple-table-th">Category</th>
              <th class="simple-table-th">Checklist</th>
              <th class="simple-table-th">Status</th>
              <th class="simple-table-th">Action</th>
            </tr>
          </thead>
        </template>

        <template v-slot:tbody>
          <tbody v-if="dataRows.length">
            <tr
              v-for="row in dataRows"
              :key="row.id"
              class="alternate-listing-row"
            >
              <td class="simple-table-td">{{ row.category_name }}</td>
              <td class="simple-table-td">{{ row.checklist_name }}</td>
              <td class="simple-table-td">
                <v-switch
                  v-model="row.activated"
                  inset
                  dense
                  color="green"
                  :true-value="1"
                  :false-value="0"
                  @change="updateStatus(row)"
                ></v-switch>
              </td>
              <td class="simple-table-td">
                <v-btn icon small color="red" @click="openDeleteDialog(row)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td colspan="4" class="text-center mt-3">
                <img
                  :src="$assetURL('media/error/empty.png')"
                  class="row-not-found-image"
                />
                <p>No categories added.</p>
              </td>
            </tr>
          </tbody>
        </template>
      </ListingTable>
    </div>
    <!-- Pagination -->
    <ListingFooter
      :dataLoading="dataLoading"
      :showingFrom="showingFrom"
      :showingTo="showingTo"
      :totalRows="totalRows"
      :currentPage="currentPage"
      :totalPages="totalPages"
    />

    <!-- Add Category Dialog -->
    <Dialog :common-dialog="addDialog" :dialog-width="700">
      <template v-slot:title>Add Category</template>
      <template v-slot:body>
        <v-container fluid class="px-6">
          <v-row align="center" no-gutters>
            <v-col>
              <v-select
                dense
                filled
                solo
                flat
                color="cyan"
                label="Category"
                :items="filteredCategories"
                item-text="text"
                item-value="id"
                v-model="selectedCategory"
              />
            </v-col>
            <v-col cols="auto" :style="{ cursor: 'pointer' }">
              <v-icon small @click="manageGroupDialog = true">mdi-cog</v-icon>
            </v-col>
          </v-row>

          <v-select
            v-if="selectedCategory"
            class="mt-4"
            dense
            filled
            solo
            flat
            color="cyan"
            label="Checklist"
            :items="filteredChecklists"
            item-text="title"
            item-value="id"
            v-model="selectedChecklist"
          />
        </v-container>
      </template>

      <template v-slot:action>
        <v-btn
          depressed
          color="cyan"
          class="white--text mx-2"
          :disabled="!selectedChecklist || form_loading"
          @click="saveCategoryChecklist"
        >
          SAVE
        </v-btn>

        <v-btn depressed class="mx-2" @click="closeAddDialog"> CLOSE </v-btn>
      </template>
    </Dialog>

    <template v-if="manageGroupDialog">
      <ManageCategory
        Equipment
        :c-dialog="manageGroupDialog"
        :category="options.categories"
        v-on:close-dialog="manageGroupDialog = false"
        v-on:get-product-category="loadCategoryOptions"
      ></ManageCategory>
    </template>

    <!-- Delete Dialog -->
    <DeleteDialog :delete-dialog="deleteDialog">
      <template v-slot:title>Delete Confirmation</template>
      <template v-slot:text>
        Are you sure you want to delete the category
        <b>{{ deleteRow.category_name }}</b
        >?
      </template>
      <template v-slot:action>
        <v-btn depressed @click="deleteDialog = false">Cancel</v-btn>
        <v-btn
          depressed
          color="red"
          class="white--text"
          :loading="delete_loading"
          @click="deleteConfirm"
        >
          Delete
        </v-btn>
      </template>
    </DeleteDialog>
  </v-container>
</template>

<script>
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import Dialog from "@/view/pages/partials/Dialog";
import DeleteDialog from "@/view/pages/partials/Delete";
import ManageCategory from "@/view/pages/partials/Manage-Product-Category.vue";

export default {
  components: {
    ListingTable,
    ListingFooter,
    Dialog,
    DeleteDialog,
    ManageCategory,
  },

  props: {
    categoriesData: {
      type: Array,
      default: () => [],
    },

    customerId: {
      type: Number,
      required: true,
    },

    options: {
      type: Object,
      default: function () {
        return {};
      },
    },
  },

  data() {
    return {
      dataLoading: false,
      addDialog: false,
      deleteDialog: false,
      selectedCategory: null,
      selectedChecklist: null,
      categoryList: [],
      checklistList: [],
      deleteRow: {},
      form_loading: false,
      delete_loading: false,
      currentPage: 1,
      paginationPageSize: 10,
      manageGroupDialog: false,
    };
  },

  computed: {
    filteredChecklists() {
      if (!this.selectedCategory) return [];

      return this.options.checklists.filter(
        (checklist) => checklist.category == this.selectedCategory
      );
    },

    filteredCategories() {
      if (!this.options.categories) return [];

      const selectedIds = this.categoriesData.map((c) => c.category_id);

      return this.options.categories.filter(
        (category) => !selectedIds.includes(category.id)
      );
    },

    totalRows() {
      return this.categoriesData ? this.categoriesData.length : 0;
    },

    totalPages() {
      if (!this.totalRows) return 1;

      return Math.ceil(this.totalRows / this.paginationPageSize);
    },

    dataRows() {
      if (!this.categoriesData) return [];

      const start = (this.currentPage - 1) * this.paginationPageSize;
      const end = start + this.paginationPageSize;

      return this.categoriesData.slice(start, end);
    },

    showingFrom() {
      if (!this.totalRows) return 0;

      return (this.currentPage - 1) * this.paginationPageSize + 1;
    },

    showingTo() {
      const end = this.currentPage * this.paginationPageSize;

      return end > this.totalRows ? this.totalRows : end;
    },
  },

  methods: {
    openAddDialog() {
      this.addDialog = true;
      this.selectedCategory = null;
      this.selectedChecklist = null;
    },

    loadCategoryOptions() {
      this.$emit("reloadOptions");
    },

    updateActivated(row) {
      const updated = this.categoriesData.map((cat) => {
        if (
          cat.category_id == row.category_id &&
          cat.checklist_id == row.checklist_id
        ) {
          return { ...cat, activated: row.activated };
        }

        return cat;
      });

      this.$emit("saveCategories", updated);
    },

    closeAddDialog() {
      this.addDialog = false;
    },

    saveCategoryChecklist() {
      if (this.form_loading) return;

      this.form_loading = true;

      const newEntry = {
        category_id: this.selectedCategory,
        category_name:
          this.options.categories.find((c) => c.id == this.selectedCategory)
            ?.text || "",
        checklist_id: this.selectedChecklist,
        checklist_name:
          this.options.checklists.find((c) => c.id == this.selectedChecklist)
            ?.title || "",
        activated: 1,
      };

      const updatedCategories = [...this.categoriesData, newEntry];

      this.$emit("saveCategories", updatedCategories);

      this.closeAddDialog();
      this.form_loading = false;
    },

    openDeleteDialog(row) {
      this.deleteRow = row;
      this.deleteDialog = true;
    },

    deleteConfirm() {
      this.delete_loading = true;

      const updatedCategories = this.categoriesData.filter(
        (c) =>
          !(
            c.category_id === this.deleteRow.category_id &&
            c.checklist_id === this.deleteRow.checklist_id
          )
      );

      this.$emit("saveCategories", updatedCategories);

      this.deleteDialog = false;
      this.delete_loading = false;
    },
  },
};
</script>
